// Pagina principal del modulo leads-import.
// Flow: subir CSV -> preview con mapping editable -> confirmar -> watch progress.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Upload, FileText, CheckCircle2, AlertCircle, Loader2,
  Play, RefreshCw, ListChecks, ChevronRight,
} from 'lucide-react';

import {
  ApiError,
  CANONICAL_FIELDS,
  commitImport,
  getImport,
  listImports,
  previewImport,
  type ImportDetail,
  type ImportSummary,
  type PreviewResult,
} from '../api';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;
type Step = 'upload' | 'mapping' | 'progress' | 'detail';

interface Props {
  addNotification?: Notifier;
}

export function LeadImportPage({ addNotification }: Props) {
  const [step, setStep] = useState<Step>('upload');
  const [imports, setImports] = useState<ImportSummary[]>([]);
  const [loading, setLoading] = useState(false);

  // Upload state
  const [file, setFile] = useState<File | null>(null);
  const [sourceLabel, setSourceLabel] = useState('');
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null);

  // Preview state
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  // Detail state
  const [detail, setDetail] = useState<ImportDetail | null>(null);

  const note = (type: 'success' | 'error' | 'info', title: string, message?: string) =>
    addNotification?.({ type, title, message });

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      setImports(await listImports(50));
    } catch (e: any) {
      note('error', 'No se pudo cargar la lista', e instanceof ApiError ? e.message : e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshList(); }, [refreshList]);

  // ── Step 1: upload + preview ──────────────────────────────────
  const handlePreview = async () => {
    if (!file) return;
    setBusy('preview');
    try {
      const result = await previewImport(file, sourceLabel.trim());
      setPreview(result);
      setMapping(result.suggested_mapping);
      setStep('mapping');
      if (result.llm_error) {
        note('info', 'Mapping LLM no disponible', `${result.llm_error.slice(0, 200)}. Mapea las columnas manualmente.`);
      } else {
        note('success', 'Preview OK', `${result.total_rows} filas detectadas. Revisa el mapping y confirma.`);
      }
    } catch (e: any) {
      note('error', 'Falló preview', e instanceof ApiError ? e.message : e?.message);
    } finally {
      setBusy(null);
    }
  };

  // ── Step 2: confirmar mapping ─────────────────────────────────
  const handleCommit = async () => {
    if (!file || !preview) return;
    if (!Object.values(mapping).some((v) => v === 'email' || v === 'phone')) {
      note('error', 'Mapping incompleto', 'Mapea al menos una columna a "email" o "phone" — sin contacto no se puede crear el Lead.');
      return;
    }
    setBusy('commit');
    try {
      const result = await commitImport(preview.import_id, file, mapping);
      note('success', 'Procesamiento iniciado', result.detail);
      // Pasa a progress y refresca lista
      setStep('progress');
      await refreshList();
      // Auto-watch del nuevo import
      setTimeout(() => loadDetail(preview.import_id), 1000);
    } catch (e: any) {
      note('error', 'Falló commit', e instanceof ApiError ? e.message : e?.message);
    } finally {
      setBusy(null);
    }
  };

  // ── Step 3: detalle de un import ──────────────────────────────
  const loadDetail = async (id: string) => {
    setLoading(true);
    try {
      const d = await getImport(id);
      setDetail(d);
      setStep('detail');
    } catch (e: any) {
      note('error', 'No se pudo cargar el detalle', e instanceof ApiError ? e.message : e?.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setFile(null);
    setSourceLabel('');
    setPreview(null);
    setMapping({});
    setDetail(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Upload size={20} style={{ color: 'var(--acc1)' }} /> Importar Leads desde CSV
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Orion mapea las columnas a campos canónicos. Cada fila se convierte
            en un Lead, con presentación + landing pública generadas en automático.
          </p>
        </div>
        <button onClick={refreshList} className="o-btn-ghost flex items-center gap-1.5 px-3 py-2 text-sm">
          <RefreshCw size={14} /> Recargar
        </button>
      </div>

      {/* Stepper visual */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Stepper active={step === 'upload'} done={!!preview} label="Subir CSV" />
        <ChevronRight size={12} />
        <Stepper active={step === 'mapping'} done={!!detail} label="Confirmar mapping" />
        <ChevronRight size={12} />
        <Stepper active={step === 'progress' || step === 'detail'} done={false} label="Procesar" />
      </div>

      {step === 'upload' && (
        <UploadStep
          file={file}
          setFile={setFile}
          sourceLabel={sourceLabel}
          setSourceLabel={setSourceLabel}
          busy={busy}
          onPreview={handlePreview}
        />
      )}

      {step === 'mapping' && preview && (
        <MappingStep
          preview={preview}
          mapping={mapping}
          setMapping={setMapping}
          busy={busy}
          onCommit={handleCommit}
          onCancel={reset}
        />
      )}

      {step === 'detail' && detail && (
        <DetailStep
          detail={detail}
          onReload={() => loadDetail(detail.id)}
          onNew={reset}
        />
      )}

      {/* Lista historica */}
      <div className="o-card">
        <h2 className="text-sm font-bold text-foreground/80 mb-3 flex items-center gap-2">
          <ListChecks size={14} /> Imports recientes ({imports.length})
        </h2>
        {loading ? (
          <div className="py-6 text-center text-muted-foreground text-sm">
            <Loader2 size={14} className="animate-spin inline mr-2" /> Cargando…
          </div>
        ) : imports.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4 text-center">Sin imports todavía. Sube tu primer CSV arriba.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-muted-foreground text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left py-2">Archivo</th>
                <th className="text-left py-2">Status</th>
                <th className="text-right py-2">Filas</th>
                <th className="text-right py-2">Creados</th>
                <th className="text-right py-2">Skipped</th>
                <th className="text-right py-2">Presentaciones</th>
                <th className="text-right py-2">Landings</th>
                <th className="text-right py-2"></th>
              </tr>
            </thead>
            <tbody>
              {imports.map((i) => (
                <tr key={i.id} className="border-t border-border/60 text-foreground/70 hover:bg-muted/40">
                  <td className="py-2"><FileText size={12} className="inline mr-1.5 text-muted-foreground/70" />{i.file_name || '—'}</td>
                  <td className="py-2"><StatusBadge status={i.status} /></td>
                  <td className="py-2 text-right">{i.total_rows}</td>
                  <td className="py-2 text-right text-emerald-600 dark:text-emerald-400">{i.leads_created}</td>
                  <td className="py-2 text-right text-amber-600 dark:text-amber-400">{i.leads_skipped}</td>
                  <td className="py-2 text-right">{i.presentations_generated}</td>
                  <td className="py-2 text-right">{i.landings_generated}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => loadDetail(i.id)} className="text-[var(--acc1)] hover:underline text-[11px]">
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Sub-componentes
// ─────────────────────────────────────────────────────────────────

function Stepper({ active, done, label }: { active: boolean; done: boolean; label: string }) {
  let cls = 'text-muted-foreground/70';
  if (done) cls = 'text-emerald-600 dark:text-emerald-400';
  else if (active) cls = 'text-[var(--acc1)] font-medium';
  return <span className={cls}>{label}</span>;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    uploaded: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
    mapping: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
    queued: 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30',
    processing: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
    done: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    failed: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30',
    cancelled: 'bg-gray-500/15 text-gray-600 dark:text-gray-300 border-gray-500/30',
  };
  const cls = colors[status] || 'bg-muted text-muted-foreground border-border';
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${cls}`}>
      {status}
    </span>
  );
}

function UploadStep({
  file, setFile, sourceLabel, setSourceLabel, busy, onPreview,
}: {
  file: File | null;
  setFile: (f: File | null) => void;
  sourceLabel: string;
  setSourceLabel: (v: string) => void;
  busy: 'preview' | 'commit' | null;
  onPreview: () => void;
}) {
  return (
    <div className="o-card space-y-4">
      <div>
        <label className="block">
          <span className="text-[11px] text-muted-foreground uppercase tracking-wider">Archivo CSV</span>
          <div className="mt-2 flex items-center gap-3">
            <input
              type="file"
              accept=".csv,.tsv,.txt"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-foreground/70 file:mr-3 file:py-2 file:px-4 file:rounded-lg
                         file:border-0 file:bg-[var(--acc1)]/20 file:text-[var(--acc1)]
                         hover:file:bg-[var(--acc1)]/30 cursor-pointer"
            />
            {file && (
              <span className="text-xs text-muted-foreground">
                {file.name} · {(file.size / 1024).toFixed(1)} KB
              </span>
            )}
          </div>
        </label>
        <p className="mt-2 text-[10px] text-muted-foreground/70">
          Formatos: CSV / TSV / TXT (max 10 MB). Cualquier estructura — Orion infiere las columnas.
        </p>
      </div>

      <div>
        <label className="block">
          <span className="text-[11px] text-muted-foreground uppercase tracking-wider">Etiqueta source (opcional)</span>
          <input
            type="text"
            value={sourceLabel}
            onChange={(e) => setSourceLabel(e.target.value)}
            placeholder="Ej: expo_thot_apr2026, listado_constructoras_qro"
            className="input-base mt-1 max-w-md"
          />
        </label>
        <p className="mt-1 text-[10px] text-muted-foreground/70">
          Útil para tracking. Si la dejas vacía, se autogenera del nombre del archivo.
        </p>
      </div>

      <button
        onClick={onPreview}
        disabled={!file || busy === 'preview'}
        className="px-4 py-2 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 text-[var(--acc1)] flex items-center gap-2 w-fit disabled:opacity-50"
      >
        {busy === 'preview' ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
        Generar preview
      </button>
    </div>
  );
}

function MappingStep({
  preview, mapping, setMapping, busy, onCommit, onCancel,
}: {
  preview: PreviewResult;
  mapping: Record<string, string>;
  setMapping: (m: Record<string, string>) => void;
  busy: 'preview' | 'commit' | null;
  onCommit: () => void;
  onCancel: () => void;
}) {
  const updateField = (col: string, target: string) => {
    setMapping({ ...mapping, [col]: target });
  };
  const customExtra = (col: string) => {
    const k = window.prompt(`Nombre del campo extra para "${col}":`, col.toLowerCase().replace(/\s+/g, '_'));
    if (k && k.trim()) updateField(col, `extra:${k.trim()}`);
  };
  const isExtra = (val: string) => val?.startsWith('extra:');
  const hasContact = useMemo(() => Object.values(mapping).some((v) => v === 'email' || v === 'phone'), [mapping]);

  return (
    <div className="space-y-4">
      <div className="o-card">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-sm font-bold text-foreground">Mapping de columnas</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {preview.total_rows} filas · {preview.inferred_columns.length} columnas detectadas.
              {preview.llm_used
                ? ' Orion sugirió un mapping inicial — revísalo.'
                : ' Sin sugerencia de Orion (mapping manual).'}
            </p>
          </div>
          {!hasContact && (
            <span className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1 shrink-0">
              <AlertCircle size={11} /> Necesitas mapear email o phone
            </span>
          )}
        </div>

        <table className="w-full text-xs">
          <thead className="text-muted-foreground text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left py-2 w-1/4">Columna CSV</th>
              <th className="text-left py-2 w-1/3">Mapea a</th>
              <th className="text-left py-2">Muestras</th>
            </tr>
          </thead>
          <tbody>
            {preview.inferred_columns.map((col) => (
              <tr key={col} className="border-t border-border/60">
                <td className="py-2 text-foreground/80 font-mono">{col}</td>
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <select
                      value={isExtra(mapping[col] || '') ? '__custom__' : (mapping[col] || '')}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') customExtra(col);
                        else updateField(col, e.target.value);
                      }}
                      className="input-base py-1 text-xs"
                    >
                      {CANONICAL_FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.group ? `[${f.group}] ` : ''}{f.label}
                        </option>
                      ))}
                      <option value="__custom__">— Campo extra personalizado —</option>
                    </select>
                    {isExtra(mapping[col] || '') && (
                      <code className="text-[10px] text-emerald-600 dark:text-emerald-400">{mapping[col]}</code>
                    )}
                  </div>
                </td>
                <td className="py-2 text-muted-foreground text-[11px] truncate max-w-md">
                  {preview.sample_rows.slice(0, 2).map((r, i) => (
                    <div key={i} className="truncate">{r[col] || '—'}</div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={onCommit}
          disabled={!hasContact || busy === 'commit'}
          className="px-4 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 flex items-center gap-2 disabled:opacity-50"
        >
          {busy === 'commit' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Confirmar e iniciar procesamiento
        </button>
        <button onClick={onCancel} className="o-btn-ghost px-4 py-2 text-sm">
          Cancelar
        </button>
      </div>
    </div>
  );
}

function DetailStep({
  detail, onReload, onNew,
}: {
  detail: ImportDetail;
  onReload: () => void;
  onNew: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="o-card">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-sm font-bold text-foreground flex items-center gap-2">
              <FileText size={14} /> {detail.file_name || detail.id.slice(0, 8)}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Source label: <code className="text-muted-foreground">{detail.source_label}</code> ·
              Solicitado por {detail.created_by_email || '—'} ·
              {new Date(detail.created_at).toLocaleString('es-MX')}
            </p>
          </div>
          <StatusBadge status={detail.status} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
          <Metric label="Filas totales" value={detail.total_rows} />
          <Metric label="Leads creados" value={detail.leads_created} accent="emerald" />
          <Metric label="Skipped" value={detail.leads_skipped} accent="amber" />
          <Metric label="Presentaciones" value={detail.presentations_generated} />
          <Metric label="Landings" value={detail.landings_generated} />
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button onClick={onReload} className="o-btn-ghost flex items-center gap-1.5 px-3 py-1.5 text-xs">
            <RefreshCw size={12} /> Refrescar
          </button>
          <button onClick={onNew} className="o-btn-ghost flex items-center gap-1.5 px-3 py-1.5 text-xs">
            <Upload size={12} /> Nuevo import
          </button>
        </div>
      </div>

      {detail.errors.length > 0 && (
        <div className="o-card border-red-500/20">
          <h3 className="text-sm font-bold text-red-600 dark:text-red-400 flex items-center gap-2 mb-2">
            <AlertCircle size={14} /> Errores ({detail.errors.length})
          </h3>
          <ul className="space-y-1 max-h-60 overflow-y-auto">
            {detail.errors.slice(0, 50).map((e, i) => (
              <li key={i} className="text-[11px] text-muted-foreground">
                <span className="text-red-600 dark:text-red-400">Fila {e.row_index + 1}:</span> {e.error_message}
              </li>
            ))}
            {detail.errors.length > 50 && (
              <li className="text-[10px] text-muted-foreground/70 italic">… y {detail.errors.length - 50} más</li>
            )}
          </ul>
        </div>
      )}

      <details className="o-card">
        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
          Ver mapping confirmado ({Object.keys(detail.confirmed_mapping).length} columnas)
        </summary>
        <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
          {Object.entries(detail.confirmed_mapping).map(([col, target]) => (
            <div key={col} className="flex justify-between border-b border-border/60 py-1">
              <code className="text-muted-foreground">{col}</code>
              <code className="text-[var(--acc1)]">{target || '(ignorado)'}</code>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

function Metric({ label, value, accent }: { label: string; value: number; accent?: 'emerald' | 'amber' }) {
  const colors = accent === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : accent === 'amber' ? 'text-amber-600 dark:text-amber-400' : 'text-foreground';
  return (
    <div className="p-3 rounded-lg bg-muted/40 border border-border/50">
      <div className={`text-2xl font-bold ${colors}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

export default LeadImportPage;
